import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase as cloudClient } from '@/lib/supabase';
import { runPipeline, getPipelineStatus, getPipelineRuns, type PipelineRun } from '@/lib/pipeline-api';
import { uploadFile } from '@/lib/utils';

export default function BrandIntelligence() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [sourceType, setSourceType] = useState<string>('');
  const [sourceQuery, setSourceQuery] = useState('');
  const [brandName, setBrandName] = useState('');
  const [productName, setProductName] = useState('');
  const [forBrand, setForBrand] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<PipelineRun | null>(null);

  // Poll active run
  useEffect(() => {
    if (!activeRunId) return;
    const interval = setInterval(async () => {
      try {
        const status = await getPipelineStatus(activeRunId);
        setActiveRun(status);
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          queryClient.invalidateQueries({ queryKey: ['pipeline-runs'] });
        }
      } catch (e) {
        console.error('Poll error', e);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeRunId, queryClient]);

  // Fetch run history
  const { data: runs = [] } = useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: getPipelineRuns,
  });

  const brandRuns = runs.filter((r) => r.file_path?.startsWith('brand-uploads/'));

  const handleSubmit = useCallback(async () => {
    if (!file || !sourceType || !sourceQuery || !brandName || !productName) {
      toast({ title: 'Missing fields', description: 'Please fill in all required fields and upload a file.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const timestamp = Date.now();
      const path = `brand-uploads/${timestamp}-${file.name}`;

      await uploadFile(file, 'kalodata-uploads', path);

      const { data: signedData } = await cloudClient.storage
        .from('kalodata-uploads')
        .createSignedUrl(path, 3600);
      const signedUrl = signedData?.signedUrl;
      if (!signedUrl) throw new Error('Failed to get signed URL');

      const body: Record<string, string> = {
        file_path: signedUrl,
        file_name: file.name,
        source_type: sourceType,
        source_query: sourceQuery,
        brand_name: brandName,
        product_name: productName,
      };
      if (sourceType === 'competitor' && forBrand) {
        body.for_brand = forBrand;
      }

      const result = await runPipeline(body);

      setActiveRunId(result.run_id);
      setActiveRun(result);
      toast({ title: 'Pipeline started', description: `Run ID: ${result.run_id}` });
      setFile(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }, [file, sourceType, sourceQuery, brandName, productName, forBrand, toast]);

  const isFormValid = !!file && !!sourceType && !!sourceQuery && !!brandName && !!productName;

  const progressPercent = activeRun?.progress
    ? [activeRun.progress.gemini_analyzed, activeRun.progress.claude_frameworks, activeRun.progress.uploaded_to_db].filter(Boolean).length / 3 * 100
    : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Brand Intelligence</h1>

      {/* Upload Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload & Analyze</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data type</Label>
              <Select value={sourceType} onValueChange={setSourceType}>
                <SelectTrigger><SelectValue placeholder="Select data type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top_100">Top 100</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="keyword">Keyword</SelectItem>
                  <SelectItem value="competitor">Competitor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>What you searched in Kalodata (e.g. 'Goli ashwagandha')</Label>
              <Input value={sourceQuery} onChange={(e) => setSourceQuery(e.target.value)} placeholder="e.g. Goli ashwagandha" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Brand shown in these videos (e.g. 'Goli')</Label>
              <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g. Goli" />
            </div>
            <div className="space-y-2">
              <Label>Specific product shown (e.g. 'Goli Ashwagandha Gummies')</Label>
              <Input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g. Goli Ashwagandha Gummies" />
            </div>
          </div>

          {sourceType === 'competitor' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client brand this data serves (e.g. 'Nello')</Label>
                <Input value={forBrand} onChange={(e) => setForBrand(e.target.value)} placeholder="e.g. Nello" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Upload File (.xlsx, .csv)</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={uploading || !isFormValid}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Run Pipeline'}
          </Button>
        </CardContent>
      </Card>

      {/* Active Run */}
      {activeRun && activeRun.status !== 'completed' && activeRun.status !== 'failed' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Active Run — {activeRun.run_id}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Progress value={progressPercent} className="h-2" />
            <div className="flex gap-3 text-sm">
              <StepBadge done={activeRun.progress?.gemini_analyzed} label="Gemini" />
              <StepBadge done={activeRun.progress?.claude_frameworks} label="Claude" />
              <StepBadge done={activeRun.progress?.uploaded_to_db} label="Uploaded" />
            </div>
            {activeRun.progress?.message && (
              <p className="text-sm text-muted-foreground">{activeRun.progress.message}</p>
            )}
            {activeRun.error && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" /> {activeRun.error}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Run History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Run History</CardTitle>
        </CardHeader>
        <CardContent>
          {brandRuns.length === 0 ? (
            <p className="text-muted-foreground text-sm">No brand intelligence runs yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brandRuns.map((run) => (
                  <TableRow key={run.run_id}>
                    <TableCell className="font-medium">{run.file_name || '—'}</TableCell>
                    <TableCell><StatusBadge status={run.status} /></TableCell>
                    <TableCell>{run.progress?.uploaded ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{run.created_at ? new Date(run.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{run.updated_at && run.status === 'completed' ? new Date(run.updated_at).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StepBadge({ done, label }: { done?: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1 text-sm">
      {done ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <span className="h-4 w-4 rounded-full border border-muted-foreground/40 inline-block" />}
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    completed: { variant: 'default', label: 'Completed' },
    running: { variant: 'secondary', label: 'Running' },
    failed: { variant: 'destructive', label: 'Failed' },
    pending: { variant: 'outline', label: 'Pending' },
  };
  const info = map[status] || { variant: 'outline' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}
